// src/pages/VerdictVault.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Search, Loader2, ArrowLeft } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import TrialCardArchive from '../components/TrialCardArchive';
import VerdictMemeFrame, { deriveOverlayVerdictLine } from '../components/VerdictMemeFrame';
import { resolveDailyVerdictRenderState } from '../utils/dailyVerdictRender';
import { deriveTrialVerdictRenderData, getCanonicalRenderedVerdictAsset } from '../utils/verdictAssets';

const FILTERS = {
  ALL: 'all',
  DAILY: 'daily',
  TRIALS: 'trials',
};

const TRIALS_PAGE_SIZE = 30;
const DAILY_PAGE_SIZE = 24;

const VAULT_DEBUG = process.env.NODE_ENV !== 'production';

function normalizeVerdictRow(row) {
  const vd = row?.verdict_data && typeof row.verdict_data === 'object' ? row.verdict_data : null;
  const renderData = deriveTrialVerdictRenderData(row);
  const imageUrl = renderData.imageUrl;
  const verdictType = row?.verdict_type ?? vd?.verdictType ?? 'VERDICT';
  const winnerPct = row?.winner_pct ?? vd?.winnerPct ?? null;
  const captionFull = renderData.captionFull;
  const captionShort = renderData.captionShort ?? deriveOverlayVerdictLine(captionFull);
  const winnerRaw = row?.winner ?? vd?.winner_label ?? null;
  const winnerLabel = (winnerRaw === 'A' || winnerRaw === 'B') ? (vd?.winner_label ?? null) : winnerRaw;
  const generatedAt = vd?.generated_at ?? vd?.generatedAt ?? row?.generated_at ?? null;
  const usedVerdictData = Boolean(vd && (vd?.verdict_line || vd?.punchline || vd?.share_card_url || vd?.background_image_url || vd?.aiImage || vd?.verdict_image_url || vd?.ai_image || vd?.image_url || vd?.verdictType));
  const missingFields = [];
  if (!captionFull) missingFields.push('caption');
  if (!imageUrl) missingFields.push('image_url');
  return {
    trial_id: String(row?.trial_id || row?.id || ''),
    image_url: imageUrl,
    verdict_type: verdictType,
    winner_pct: winnerPct,
    winner_label: winnerLabel,
    caption_full: captionFull,
    caption_short: captionShort,
    generated_at: generatedAt,
    _used_verdict_data: usedVerdictData,
    _missing_fields: missingFields,
    _row_keys: row ? Object.keys(row) : [],
    _verdict_data_keys: vd ? Object.keys(vd) : [],
  };
}

function DailyDecisionCard({ row }) {
  const title = row.question || 'Daily Decision';
  const image = row.renderable_image_url || null;
  const options = Array.isArray(row.options) ? row.options.filter(Boolean) : [];
  const winnerIdx = Number.isFinite(Number(row.winning_option_index)) ? Number(row.winning_option_index) : 0;
  const winningOption = options[winnerIdx] || null;
  const losingOptions = options.filter((_, idx) => idx !== winnerIdx);
  const joinOptionLabels = (labels = []) => {
    const items = labels.map((label) => String(label || '').trim()).filter(Boolean);
    if (items.length === 0) return '';
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
  };
  const losingField = joinOptionLabels(losingOptions);
  const compactFieldLabel = losingField.length > 42 ? 'the field' : losingField;
  const hasThreeOptionField = losingOptions.length >= 2;
  const genericFallbackText = String(row.renderable_text || '').trim();
  const legacyGeneratedText = String(row.meme_snark_text || genericFallbackText || '').trim();
  const localFieldAwareSummary = winningOption
    ? `${winningOption} beat ${compactFieldLabel || 'the field'}.`
    : (genericFallbackText || null);
  const snark = hasThreeOptionField
    ? localFieldAwareSummary
    : (genericFallbackText || null);
  const hasStaleGeneratedPreview =
    hasThreeOptionField
    && Boolean(image)
    && Boolean(legacyGeneratedText)
    && legacyGeneratedText !== localFieldAwareSummary;
  const previewImage = hasStaleGeneratedPreview ? null : image;
  const previewAssetSource = hasStaleGeneratedPreview
    ? {
        ...row,
        renderable_image_url: null,
        meme_image_url: null,
        share_card_url: null,
        final_image_url: null,
        background_image_url: null,
        media_asset_url: null,
        image_url: null,
        imageUrl: null,
      }
    : row;
  const totalVotes = Number(row.total_votes || 0);
  const status = row.meme_status || 'pending';
  const verdictReady = Boolean(row.verdict_ready);
  const verdictFailed = Boolean(row.verdict_failed);
  const verdictProcessing = !verdictReady && !verdictFailed;

  if (!verdictReady && verdictProcessing && process.env.NODE_ENV !== 'production') {
    console.log('[vault_daily_render_fallback_blocked]', {
      decision_id: row.decision_id || row.id,
      meme_status: status,
      has_image: Boolean(row.meme_image_url),
      has_text: Boolean(row.meme_snark_text),
    });
  }

  const renderState = verdictFailed ? 'failed' : (verdictReady ? 'ready' : 'processing');
  const secondaryFieldLine = hasThreeOptionField && losingOptions.length > 0
    ? `Also in the room: ${joinOptionLabels(losingOptions)}`
    : null;

  return (
    <Link
      to={`/daily/${row.decision_id || row.id}?source=vault`}
      className="block rounded-xl overflow-hidden bg-slate-800 border border-slate-700 hover:border-slate-600 transition"
    >
      {hasThreeOptionField ? (
        <div className="border-b border-slate-700/80 bg-[linear-gradient(180deg,rgba(88,28,135,0.22),rgba(15,23,42,0.98))]">
          {previewImage ? (
            <div className="relative">
              <img
                src={previewImage}
                alt={title}
                className="block h-40 w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/15 to-transparent" />
            </div>
          ) : null}
          <div className="p-4">
            <div className="text-[11px] uppercase tracking-[0.18em] text-purple-300 font-bold">Daily Decision</div>
            <div className="mt-2 text-slate-100 text-xl font-black leading-tight line-clamp-2">
              {winningOption || 'Winner'}
            </div>
            {snark && verdictReady ? (
              <div className="mt-2 text-sm text-slate-200 font-medium line-clamp-2">{snark}</div>
            ) : null}
            {secondaryFieldLine && verdictReady ? (
              <div className="mt-2 text-xs text-slate-400 line-clamp-2">{secondaryFieldLine}</div>
            ) : null}
          </div>
        </div>
      ) : (
        <VerdictMemeFrame
          id={row.decision_id || row.id}
          title={title}
          sideA={options[0] || null}
          sideB={options[1] || null}
          verdictLine={snark || null}
          verdictLineShort={snark || null}
          verdictLineFull={snark || null}
          imageUrl={previewImage}
          assetSource={previewAssetSource}
          winnerLabel={winningOption || 'Winner'}
          loserLabel={losingOptions[0] || 'Runner-up'}
          isDaily
          isClose={false}
          isLandslide={false}
          showEmoji={false}
          renderState={renderState}
          heightClass="h-56"
          surface="vault-daily"
        />
      )}
      <div className="p-3">
        <div className="text-slate-100 font-semibold line-clamp-2 mt-1">{title}</div>
        {!hasThreeOptionField && snark && verdictReady ? <div className="text-slate-300 text-sm mt-1 line-clamp-2">{snark}</div> : null}
        <div className="text-xs text-slate-400 mt-2">{totalVotes} votes</div>
      </div>
    </Link>
  );
}

export default function VerdictVault() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sort, setSort] = useState('new');
  const [activeFilter, setActiveFilter] = useState(FILTERS.ALL); // [VERDICT_VAULT_FILTER_RESTORE]
  const [trialFilter, setTrialFilter] = useState('all'); // all | mine | voted
  const [trialRows, setTrialRows] = useState([]);
  const [dailyRows, setDailyRows] = useState([]);
  const [trialHasMore, setTrialHasMore] = useState(true);
  const [dailyHasMore, setDailyHasMore] = useState(true);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [votedTrialIds, setVotedTrialIds] = useState(() => new Set());

  const loadVault = async ({ reset = false, target = 'all' } = {}) => {
    if (reset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      const terminalStatuses = ['ended', 'closed', 'completed', 'verdict_ready', 'sudden_death_failed'];
      const shouldLoadTrials = target === 'all' || target === 'trials';
      const shouldLoadDaily = target === 'all' || target === 'daily';
      const trialOffset = reset ? 0 : trialRows.length;
      const dailyOffset = reset ? 0 : dailyRows.length;

      const [archiveRes, dailyRes] = await Promise.all([
        shouldLoadTrials
          ? supabase
              .from('trials')
              .select('id,title,side_a,side_b,image_emoji,votes_a,votes_b,total_votes,closed_at,created_at,created_by,creator_id,verdict_data')
              .in('status', terminalStatuses)
              .order(sort === 'top' ? 'total_votes' : 'closed_at', { ascending: false })
              .order('created_at', { ascending: false })
              .range(trialOffset, trialOffset + TRIALS_PAGE_SIZE - 1)
          : Promise.resolve({ data: null, error: null }),
        shouldLoadDaily
          ? supabase
              .from('daily_decisions')
              .select('id,decision_date,question,options,status,revealed_at,created_at,daily_decision_results(decision_id,total_votes,winning_option_index,meme_status,meme_image_url,meme_snark_text,verdict_generated_at)')
              .in('status', ['closed_pending_verdict', 'verdict_ready', 'revealed', 'archived'])
              .order('decision_date', { ascending: false })
              .order('created_at', { ascending: false })
              .range(dailyOffset, dailyOffset + DAILY_PAGE_SIZE - 1)
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (archiveRes.error) throw archiveRes.error;
      if (dailyRes.error) throw dailyRes.error;

      const archiveRows = archiveRes.data || [];
      const trialIds = archiveRows.map((r) => r.id).filter(Boolean);

      const [trialsRes, cacheRes, queueRes] = await Promise.all([
        trialIds.length
          ? supabase.from('trials').select('id,verdict_data,created_by,creator_id').in('id', trialIds)
          : Promise.resolve({ data: [], error: null }),
        trialIds.length
          ? supabase.from('verdict_cache').select('trial_id,status,generated_at,image_url,roast_punchline,sports_punchline,internet_punchline,verdict_type,winner,winner_pct').in('trial_id', trialIds)
          : Promise.resolve({ data: [], error: null }),
        trialIds.length
          ? supabase.from('verdict_queue').select('trial_id,status,attempts,last_error,updated_at,next_attempt_at').in('trial_id', trialIds)
          : Promise.resolve({ data: [], error: null }),
      ]);

      const trialMap = new Map((trialsRes.data || []).map((t) => [t.id, t]));
      const cacheMap = new Map((cacheRes.data || []).map((c) => [c.trial_id, c]));
      const queueMap = new Map((queueRes.data || []).map((q) => [q.trial_id, q]));
      let creatorCallMap = new Map();
      if (currentUserId && trialIds.length) {
        const { data: creatorCallRows } = await supabase
          .from('trial_creator_calls')
          .select('trial_id,creator_side')
          .in('trial_id', trialIds);
        creatorCallMap = new Map((creatorCallRows || []).map((row) => [row.trial_id, row]));
      }

      const enrichedTrials = archiveRows.map((row) => {
        const trial = trialMap.get(row.id) || {};
        const verdict = trial?.verdict_data || {};
        const cacheRow = cacheMap.get(row.id) || null;
        const queueRow = queueMap.get(row.id) || null;
        const creatorCallRow = creatorCallMap.get(row.id) || null;
        const queueStatus = String(queueRow?.status || '').toLowerCase();
        const normalized = normalizeVerdictRow({ ...row, ...cacheRow, verdict_data: verdict });
        const verdictReady = Boolean(normalized.image_url);
        const verdictFailed = (queueStatus === 'failed' || queueStatus === 'permanently_failed') && !verdictReady;
        const verdictProcessing = !verdictReady && !verdictFailed;
        const verdictImage = normalized.image_url || null;
        const verdictPunchline = normalized.caption_full || null;
        const canonicalVerdictAt = normalized.generated_at || null;
        const votesA = Number(row.votes_a || 0);
        const votesB = Number(row.votes_b || 0);

        const creatorIdCanonical =
          row.created_by ||
          row.creator_id ||
          trial?.created_by ||
          trial?.creator_id ||
          null;
        const creatorCallSide = String(creatorCallRow?.creator_side || '').toUpperCase();
        const creatorCallLabel = creatorCallSide === 'A'
          ? row.side_a
          : creatorCallSide === 'B'
            ? row.side_b
            : null;
        const creatorCallOutcome = creatorCallSide
          ? (votesA === votesB
            ? 'push'
            : ((votesA > votesB && creatorCallSide === 'A') || (votesB > votesA && creatorCallSide === 'B') ? 'won' : 'lost'))
          : null;
        return {
          ...row,
          image_url: verdictImage,
          image_emoji: row.image_emoji || '🧑‍⚖️',
          verdict_data: verdict,
          punchline: verdictPunchline,
          verdict_type: normalized.verdict_type,
          winner_pct: normalized.winner_pct,
          winner_label: normalized.winner_label,
          generated_at: normalized.generated_at,
          caption: normalized.caption_full,
          caption_short: normalized.caption_short,
          caption_full: normalized.caption_full,
          verdict_line_full: normalized.caption_full,
          verdict_line_short: normalized.caption_short,
          trial_id: normalized.trial_id,
          creator_id_canonical: creatorIdCanonical,
          item_type: 'trial',
          creator_call_side: creatorCallSide || null,
          creator_call_label: creatorCallLabel,
          creator_call_outcome: creatorCallOutcome,
          verdict_ready: verdictReady,
          verdict_failed: verdictFailed,
          verdict_processing: verdictProcessing,
          renderable_image_url: verdictImage,
          renderable_text: verdictPunchline,
          canonical_verdict_at: canonicalVerdictAt,
        };
      });

      if (VAULT_DEBUG) {
        enrichedTrials
          .filter((row) => String(row?.verdict_data?.image_source || '').toLowerCase() === 'ai_primary')
          .slice(0, 10)
          .forEach((row) => {
            const asset = getCanonicalRenderedVerdictAsset(row);
            const renderedAsset = deriveTrialVerdictRenderData(row);
            console.log('[vault_premium_asset_selected]', {
              trial_id: row.id,
              image_source: row?.verdict_data?.image_source || null,
              selected_asset_url: renderedAsset.imageUrl || null,
              selected_asset_source: asset.source || null,
              share_card_url: row?.verdict_data?.share_card_url || null,
              composed_share_card_url: row?.verdict_data?.composed_share_card_url || null,
              background_image_url: row?.verdict_data?.background_image_url || null,
              caption_full: row?.caption_full || null,
            });
          });
      }

      if (VAULT_DEBUG) {
        enrichedTrials.slice(0, 5).forEach((row) => {
          const normalized = normalizeVerdictRow(row);
          if (normalized._used_verdict_data) {
            console.log('[vault_normalize_used_verdict_data]', {
              trial_id: normalized.trial_id,
              missing: normalized._missing_fields,
            });
          }
          if (normalized._missing_fields.length > 0) {
            console.log('[vault_normalize_missing_fields]', {
              trial_id: normalized.trial_id,
              missing: normalized._missing_fields,
              row_keys: normalized._row_keys,
              verdict_data_keys: normalized._verdict_data_keys,
            });
          }
        });
      }

      const normalizedDaily = (dailyRes.data || []).map((r) => {
        const resultRow = Array.isArray(r.daily_decision_results)
          ? (r.daily_decision_results[0] || null)
          : (r.daily_decision_results || null);
        const renderState = resolveDailyVerdictRenderState(resultRow, r);
        return {
          decision_id: resultRow?.decision_id || r.id,
          total_votes: resultRow?.total_votes ?? 0,
          winning_option_index: resultRow?.winning_option_index ?? null,
          meme_status: resultRow?.meme_status || 'pending',
          meme_image_url: resultRow?.meme_image_url || null,
          meme_snark_text: resultRow?.meme_snark_text || null,
          verdict_generated_at: resultRow?.verdict_generated_at || null,
          question: r.question,
          options: r.options || null,
          status: r.status,
          decision_date: r.decision_date,
          revealed_at: r.revealed_at || null,
          item_type: 'daily',
          verdict_ready: renderState.verdictReady,
          verdict_failed: renderState.verdictFailed,
          verdict_processing: renderState.verdictProcessing,
          renderable_image_url: renderState.imageUrl,
          renderable_asset_source: { ...(resultRow || {}), ...(r || {}) },
          renderable_text: renderState.text,
          canonical_verdict_at: renderState.canonicalVerdictAt,
        };
      });

      if (process.env.NODE_ENV !== 'production') {
        console.log('[vault] daily_rows_loaded', {
          count: normalizedDaily.length,
          first3: normalizedDaily.slice(0, 3).map((r) => ({ id: r.decision_id, date: r.decision_date })),
          latest_decision_date: normalizedDaily[0]?.decision_date || null,
        });
      }

      // Batch fetch creator usernames for the follow button
      const uniqueCreatorIds = [...new Set(enrichedTrials.map(t => t.creator_id_canonical).filter(Boolean))];
      let creatorUsernameMap = {};
      if (uniqueCreatorIds.length > 0) {
        const { data: creatorData } = await supabase
          .from('users')
          .select('id, username')
          .in('id', uniqueCreatorIds);
        if (creatorData) {
          creatorData.forEach(u => { creatorUsernameMap[u.id] = u.username; });
        }
      }
      const enrichedWithCreators = enrichedTrials.map(t => ({
        ...t,
        creator_username: creatorUsernameMap[t.creator_id_canonical] || null,
      }));

      if (shouldLoadTrials) {
        setTrialRows((prev) => {
          if (reset) return enrichedWithCreators;
          const seen = new Set(prev.map((row) => row.id));
          return [...prev, ...enrichedWithCreators.filter((row) => !seen.has(row.id))];
        });
        setTrialHasMore(archiveRows.length === TRIALS_PAGE_SIZE);
      }

      if (shouldLoadDaily) {
        setDailyRows((prev) => {
          if (reset) return normalizedDaily;
          const seen = new Set(prev.map((row) => row.decision_id));
          return [...prev, ...normalizedDaily.filter((row) => !seen.has(row.decision_id))];
        });
        setDailyHasMore(normalizedDaily.length === DAILY_PAGE_SIZE);
      }
    } catch (e) {
      console.error('Verdict vault load failed', e);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setInitialLoad(false);
    }
  };

  useEffect(() => {
    setTrialRows([]);
    setDailyRows([]);
    setTrialHasMore(true);
    setDailyHasMore(true);
    loadVault({ reset: true, target: 'all' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data: authData } = await supabase.auth.getUser();
        const authUserId = authData?.user?.id || null;
        if (!authUserId) {
          if (alive) setCurrentUserId(null);
          return;
        }
        const { data: userRow, error: userErr } = await supabase
          .from('users')
          .select('id')
          .eq('auth_user_id', authUserId)
          .maybeSingle();
        if (userErr) throw userErr;
        if (alive) setCurrentUserId(userRow?.id || null);
      } catch {
        if (alive) setCurrentUserId(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!currentUserId) return;
      try {
        const { data: voteRows, error: voteErr } = await supabase
          .from('votes')
          .select('trial_id')
          .eq('user_id', currentUserId);
        if (voteErr) throw voteErr;
        if (!alive) return;
        const next = new Set((voteRows || []).map((r) => r.trial_id).filter(Boolean));
        setVotedTrialIds(next);
      } catch {
        if (alive) setVotedTrialIds(new Set());
      }
    })();
    return () => { alive = false; };
  }, [currentUserId]);

  useEffect(() => {
    const y = sessionStorage.getItem('vaultScrollY');
    if (y) {
      requestAnimationFrame(() => {
        window.scrollTo(0, Number(y));
        sessionStorage.removeItem('vaultScrollY');
      });
    }
  }, []);

  const filteredTrials = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    let rows = !q ? trialRows : trialRows.filter((r) => (r.title || '').toLowerCase().includes(q));
    if (trialFilter === 'mine') {
      rows = rows.filter((r) => {
        const owner = r.creator_id_canonical || null;
        return owner && currentUserId && String(owner) === String(currentUserId);
      });
    } else if (trialFilter === 'voted') {
      rows = rows.filter((r) => votedTrialIds.has(r.id));
    }
    return rows;
  }, [trialRows, searchText, trialFilter, currentUserId, votedTrialIds]);

  const creatorRecord = useMemo(() => {
    if (!currentUserId) return null;
    const creatorRows = trialRows.filter((row) => String(row.creator_id_canonical || '') === String(currentUserId));
    if (creatorRows.length === 0) return null;
    const landed = creatorRows.filter((row) => row.creator_call_outcome === 'won').length;
    const missed = creatorRows.filter((row) => row.creator_call_outcome === 'lost').length;
    const pushed = creatorRows.filter((row) => row.creator_call_outcome === 'push').length;
    return {
      callsMade: creatorRows.length,
      landed,
      missed,
      pushed,
    };
  }, [trialRows, currentUserId]);

  const filteredDaily = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return dailyRows;
    return dailyRows.filter((r) => (r.question || '').toLowerCase().includes(q));
  }, [dailyRows, searchText]);

  const mergedAll = useMemo(() => {
    if (activeFilter !== FILTERS.ALL) return [];
    const rows = [...filteredTrials, ...filteredDaily];
    const withSortKey = rows.map((row) => {
      const fallbackTs = row.item_type === 'trial'
        ? (row.closed_at || row.created_at || null)
        : (row.canonical_verdict_at || row.decision_date || row.created_at || null);
      const sortKey = row.canonical_verdict_at || fallbackTs;
      return { ...row, _sortKey: sortKey ? new Date(sortKey).getTime() : 0 };
    });
    const sorted = withSortKey.sort((a, b) => (b._sortKey || 0) - (a._sortKey || 0));
    if (process.env.NODE_ENV !== 'production') {
      sorted.slice(0, 5).forEach((row) => {
        console.log('[vault_sort_key]', {
          id: row.id || row.decision_id,
          item_type: row.item_type,
          canonical_verdict_at: row.canonical_verdict_at || null,
          sort_key: row._sortKey,
        });
      });
    }
    return sorted;
  }, [activeFilter, filteredTrials, filteredDaily]);

  const canLoadMore = useMemo(() => {
    if (activeFilter === FILTERS.ALL) return trialHasMore || dailyHasMore;
    if (activeFilter === FILTERS.DAILY) return dailyHasMore;
    return trialHasMore;
  }, [activeFilter, trialHasMore, dailyHasMore]);

  const handleLoadMore = async () => {
    if (loading || loadingMore) return;
    if (activeFilter === FILTERS.ALL) {
      await loadVault({ reset: false, target: 'all' });
      return;
    }
    if (activeFilter === FILTERS.DAILY) {
      await loadVault({ reset: false, target: 'daily' });
      return;
    }
    await loadVault({ reset: false, target: 'trials' });
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-200 text-sm font-medium"
            title="Back to main"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>
          <div />
        </div>

        <div className="mb-4">
          <h1 className="text-2xl md:text-3xl font-bold">Verdict Vault</h1>
          <div className="mt-1 text-sm text-slate-400">
            Your best receipts, biggest calls, and strongest verdicts worth sending again.
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:items-center mb-4">
          <div className="flex items-center gap-2 w-full md:w-1/2">
            <Search className="w-5 h-5 text-slate-400" />
            <input
              className="w-full bg-slate-800 text-slate-100 placeholder:text-slate-400 rounded-xl px-3 py-2 outline-none"
              placeholder="Search receipts..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <select
            className="bg-slate-800 text-slate-100 rounded-xl px-3 py-2"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            <option value="new">Latest</option>
            <option value="top">Biggest traction</option>
          </select>
        </div>

        <div className="mb-5 inline-flex rounded-xl border border-slate-700 bg-slate-800 p-1">
          <button
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${activeFilter === FILTERS.ALL ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
            onClick={() => setActiveFilter(FILTERS.ALL)}
          >
            All
          </button>
          <button
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${activeFilter === FILTERS.DAILY ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
            onClick={() => setActiveFilter(FILTERS.DAILY)}
          >
            Daily
          </button>
          <button
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${activeFilter === FILTERS.TRIALS ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
            onClick={() => setActiveFilter(FILTERS.TRIALS)}
          >
            Trials
          </button>
        </div>

        {activeFilter === FILTERS.ALL && (
          <>
            <h2 className="text-xl font-bold mb-1">Best Receipts</h2>
            <div className="mb-3 text-sm text-slate-400">The verdict moments most worth reopening, sending, or proving you called.</div>
            {mergedAll.length === 0 ? (
              <div className="text-sm text-slate-400 mb-6">No receipts yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {mergedAll.map((row) => (
                  row.item_type === 'daily'
                    ? <DailyDecisionCard key={`daily_${row.decision_id}`} row={row} />
                    : <TrialCardArchive key={row.id} trial={row} currentUserId={currentUserId} linkTo={`/t/${row.id}?source=vault`} onBeforeOpen={() => {
                      sessionStorage.setItem('vaultScrollY', String(window.scrollY));
                    }} />
                ))}
              </div>
            )}
          </>
        )}

        {activeFilter !== FILTERS.TRIALS && activeFilter !== FILTERS.ALL && (
          <>
            <h2 className="text-xl font-bold mb-1">Daily Receipts</h2>
            <div className="mb-3 text-sm text-slate-400">Quick public calls that are still worth revisiting once the verdict lands.</div>
            {filteredDaily.length === 0 ? (
              <div className="text-sm text-slate-400 mb-6">No daily receipts yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {filteredDaily.map((row) => (
                  <DailyDecisionCard key={row.decision_id} row={row} />
                ))}
              </div>
            )}
          </>
        )}

        {activeFilter !== FILTERS.DAILY && activeFilter !== FILTERS.ALL && (
          <>
            <h2 className="text-xl font-bold mb-1">Trial Receipts</h2>
            <div className="mb-3 text-sm text-slate-400">Your calls, your receipts, and the verdicts worth pulling back up when you want proof.</div>
            {currentUserId && trialFilter === 'mine' && creatorRecord && (
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-200">
                  {creatorRecord.callsMade} calls made
                </div>
                <div className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-200">
                  {creatorRecord.landed} landed
                </div>
                <div className="rounded-full border border-rose-400/25 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-200">
                  {creatorRecord.missed} missed
                </div>
                {creatorRecord.pushed > 0 && (
                  <div className="rounded-full border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-semibold text-slate-300">
                    {creatorRecord.pushed} pushed
                  </div>
                )}
              </div>
            )}
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <div className="inline-flex rounded-xl border border-slate-700 bg-slate-800 p-1">
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${trialFilter === 'all' ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                  onClick={() => setTrialFilter('all')}
                >
                  All
                </button>
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${trialFilter === 'mine' ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                  onClick={() => setTrialFilter('mine')}
                >
                  Your Calls
                </button>
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${trialFilter === 'voted' ? 'bg-purple-600 text-white' : 'text-slate-300 hover:bg-slate-700'}`}
                  onClick={() => setTrialFilter('voted')}
                >
                  You Backed
                </button>
              </div>
              {!currentUserId && (trialFilter === 'mine' || trialFilter === 'voted') && (
                <div className="text-xs text-slate-400">Sign in to filter by Your Calls or You Backed.</div>
              )}
            </div>
            {filteredTrials.length === 0 ? (
              <div className="text-sm text-slate-400">No trial receipts yet.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {filteredTrials.map((t) => (
                  <TrialCardArchive
                    key={t.id}
                    trial={t}
                    currentUserId={currentUserId}
                    linkTo={`/t/${t.id}?source=vault`}
                    onRedoTrial={async () => {
                      navigate(
                        `/create?title=${encodeURIComponent(`${t.title} (Rematch)`)}`
                        + `&sideA=${encodeURIComponent(t.side_a || '')}`
                        + `&sideB=${encodeURIComponent(t.side_b || '')}`
                        + `&emoji=${encodeURIComponent(t.image_emoji || '⚖️')}`
                        + `&mode=${encodeURIComponent(t.trial_mode || 'debate')}`
                        + `&category=${encodeURIComponent(t.decision_category || '')}`
                      );
                    }}
                    onBeforeOpen={() => {
                      sessionStorage.setItem('vaultScrollY', String(window.scrollY));
                    }}
                  />
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex items-center justify-center py-6">
          {loading && <Loader2 className="w-6 h-6 animate-spin text-slate-400" />}
          {!loading && canLoadMore && (
            <button
              onClick={handleLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 disabled:opacity-60"
            >
              {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loadingMore ? 'Loading more…' : 'Load more'}
            </button>
          )}
          {!loading && initialLoad === false && filteredTrials.length === 0 && filteredDaily.length === 0 && (
            <div className="text-slate-400 text-sm">No receipts found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
